import React, { ChangeEvent, useState } from 'react';
import { InputAdornment, TextField } from '@mui/material';
import { makeStyles } from '~/UIKit/Style';
import { Search } from '@mui/icons-material';
import {
  GRAY_DARK_SHADE_2,
  WHITE,
  PLAIN_GREY,
  combinedColor,
  OPACITY_MEDIUM,
} from '~/Constants/Colors.constants';
import { TRANSITION_MATERIAL } from '~/Constants/CSS.constants';

export interface SearchTextFieldProps {
  id?: string;
  placeholder: string;
  onChange: (event: ChangeEvent<HTMLInputElement>) => void;
  withBackground?: boolean;
  isMinimizable?: boolean;
  value?: string | number;
  autoFocus?: boolean;
}

const useStyles = ({
  withBackground,
  isMinimized,
  isMinimizable,
}: {
  withBackground?: boolean;
  isMinimized?: boolean;
  isMinimizable?: boolean;
}) =>
  makeStyles((theme) => ({
    searchTextFieldContainer: {
      padding: withBackground ? '15px 8px' : '',
      backgroundColor: withBackground ? GRAY_DARK_SHADE_2 : 'transparent',
    },
    searchField: {
      '& .MuiInputBase-adornedStart': {
        height: 37,
      },
      width: '100%',
      [theme.breakpoints.down('md')]: {
        width: !isMinimizable ? '' : isMinimized ? 37 : 200,
      },
      [theme.breakpoints.up('md')]: {
        width: !isMinimizable ? '' : isMinimized ? 280 : 450,
      },
      transition: `width ${TRANSITION_MATERIAL}`,
    },
    searchIcon: {
      width: 20,
      height: 20,
    },
    searchTextFieldRoot: {
      '& .MuiOutlinedInput-root': {
        borderRadius: 50,
        background: WHITE,
        paddingLeft: isMinimized ? 8 : '',
      },
      '& .MuiOutlinedInput-notchedOutline': {
        border: withBackground ? 'none' : `1px solid ${combinedColor(PLAIN_GREY, OPACITY_MEDIUM)}`,
      },
    },
  }));

export const SearchTextField = ({
  id,
  placeholder,
  onChange,
  withBackground = false,
  isMinimizable = false,
  value,
  autoFocus = false,
}: SearchTextFieldProps) => {
  const [isMinimized, setIsMinimized] = useState(true);
  const { classes } = useStyles({
    withBackground,
    isMinimized: isMinimized && isMinimizable,
    isMinimizable,
  })();
  const onBlur = () => {
    setIsMinimized(true);
  };
  const onFocus = () => {
    setIsMinimized(false);
  };

  return (
    <div className={classes.searchTextFieldContainer} onFocus={onFocus} onClick={onFocus}>
      <TextField
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Search className={classes.searchIcon} />
            </InputAdornment>
          ),
        }}
        classes={{ root: classes.searchTextFieldRoot }}
        className={classes.searchField}
        id={id}
        onChange={onChange}
        placeholder={placeholder}
        variant="outlined"
        onBlur={onBlur}
        value={value}
        autoFocus={autoFocus}
      />
    </div>
  );
};
